/**
 * Shared helpers for the Shopify API factories. The connection routes live
 * under `/integrations/shopify`; campaign-scoped routes are at the /api/v1 root.
 */
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/types';

export const INTEGRATION_BASE = '/integrations/shopify';


/** Read a `DataResponse<T>` envelope; throws ApiError on failure. */
export async function getData<T>(url: string): Promise<T> {
  const res = await apiClient.get<DataResponse<T>>(url);
  return res.data.data;
}

/** POST and unwrap `{ data: T }`. */
export async function postData<T>(
  url: string,
  body?: unknown,
  config?: { timeout?: number },
): Promise<T> {
  const res = await apiClient.post<DataResponse<T>>(url, body, config);
  return res.data.data;
}

export interface PagedRows<T> {
  entries: T[];
  total: number;
  page: number;
  totalPages: number;
}

/** Query string from a params object: undefined, null and blank values are
 *  dropped; `false` and `0` are kept. */
export function pagedQs(params: Record<string, string | number | boolean | undefined | null>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    qs.set(key, String(value));
  }
  return qs.toString();
}

/** Read a `common.WritePaginated` envelope. A body that is not `{data: [], pagination}`
 *  throws rather than reading as an empty list. */
export async function getPaged<T>(url: string): Promise<PagedRows<T>> {
  const res = await apiClient.get<{
    data?: unknown;
    pagination?: Partial<Record<'total' | 'page' | 'limit' | 'total_pages', unknown>>;
  }>(url);
  const body = res.data;
  const p = body?.pagination;
  const complete =
    !!p &&
    typeof p.total === 'number' &&
    typeof p.page === 'number' &&
    typeof p.limit === 'number' &&
    typeof p.total_pages === 'number';
  if (!Array.isArray(body?.data) || !complete) {
    throw new Error('Unexpected response: expected a paginated list');
  }
  return {
    entries: body.data as T[],
    total: p.total as number,
    page: p.page as number,
    totalPages: p.total_pages as number,
  };
}
